import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, StarIcon } from '../icons';
import { DESTINATION_OPTIONS, normalizeDestination } from '../../utils/destinations';
import { generateTemporaryPassword, isStrongPassword } from '../../utils/password';
import { resendCompanyAccess } from '../../services/firebase';
import Modal from '../ui/Modal';

const adminFieldClass =
  'mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-2.5 text-sm text-slate-700 shadow-none placeholder:text-slate-300 focus:border-[#004AAD] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#004AAD]/10 transition-colors';

const createEmptyCompanyForm = () => ({
    name: '',
    exportType: 'Banano',
    role: 'company',
    stars: 0,
    id: '',
    destination: '',
    accessEmail: '',
    temporaryPassword: generateTemporaryPassword(),
    contactEmail: '',
    respondent: '',
  });

const AdminUserManagement = ({
  users,
  submissions = [],
  onCreateUser,
  onReviewRegistration,
  onUpdateCompany,
  onDeleteCompany,
  onSendCompanyReminder,
  onTestTransactionalEmail,
  onNotify,
}) => {
  const [formData, setFormData] = useState(createEmptyCompanyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [busyCompanyId, setBusyCompanyId] = useState('');

  const companyUsers = users.filter((u) => u.role === 'company');
  const activeCompanyUsers = companyUsers.filter((user) => user.active !== false);
  const pendingRegistrations = companyUsers.filter(user => user.registrationStatus === 'pending');
  const filteredUsers = useMemo(
    () =>
      companyUsers.filter((user) => user.registrationStatus !== 'pending').filter((user) =>
        `${user.name} ${user.id} ${user.exportType} ${user.email} ${user.contactEmail}`.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [companyUsers, searchTerm],
  );
  const topSector =
    companyUsers
      .reduce((acc, user) => {
        if (!user.exportType) return acc;
        acc[user.exportType] = (acc[user.exportType] || 0) + 1;
        return acc;
      }, {})
  ;
  const leadingSector =
    Object.entries(topSector || {})
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 'Sin dato';
  const averageStars =
    activeCompanyUsers.length > 0 ? (activeCompanyUsers.reduce((acc, user) => acc + (user.stars || 0), 0) / activeCompanyUsers.length).toFixed(1) : '0.0';

  const countCompanySubmissions = (company) => submissions.filter(
    item => item.companyUid === company.uid || item.companyId === company.id,
  ).length;

  const openEditCompany = (company) => {
    setSelectedCompany(company);
    setEditForm({
      name: company.name || '',
      email: company.email || '',
      contactEmail: company.contactEmail || company.email || '',
      respondent: company.respondent || '',
      exportType: company.exportType || 'Otros',
      destination: company.destination || '',
      active: company.active !== false,
    });
  };

  const handleEditChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSaveCompany = async (event) => {
    event.preventDefault();
    if (!selectedCompany || !editForm) return;
    setBusyCompanyId(selectedCompany.id);
    try {
      await onUpdateCompany({
        ...selectedCompany,
        ...editForm,
        destination: normalizeDestination(editForm.destination),
      });
      onNotify(
        editForm.active ? 'Datos de la empresa actualizados.' : 'Empresa desactivada; ya no podrá iniciar sesión.',
        'success',
      );
      setSelectedCompany(null);
      setEditForm(null);
    } catch (error) {
      onNotify(error.message || 'No se pudo actualizar la empresa.', 'error');
    } finally {
      setBusyCompanyId('');
    }
  };

  const handleDeleteCompany = async (company) => {
    const submissionCount = countCompanySubmissions(company);
    if (submissionCount > 0) {
      onNotify(
        `${company.name} tiene ${submissionCount} envío${submissionCount === 1 ? '' : 's'}. Desactívala desde Editar para conservar el historial.`,
        'error',
      );
      return;
    }
    if (!window.confirm(`¿Eliminar definitivamente ${company.name}? Se borrarán su acceso y borradores. Esta acción no se puede deshacer.`)) return;
    setBusyCompanyId(company.id);
    try {
      await onDeleteCompany(company);
      onNotify('Empresa eliminada definitivamente.', 'success');
    } catch (error) {
      onNotify(error.message || 'No se pudo eliminar la empresa.', 'error');
    } finally {
      setBusyCompanyId('');
    }
  };

  const handleSendCompanyReminder = async (company) => {
    const period = new Date().toISOString().slice(0, 7);
    if (!window.confirm(`¿Enviar a ${company.name} el recordatorio individual de ${period}?`)) return;
    setBusyCompanyId(company.id);
    try {
      const result = await onSendCompanyReminder(company, period);
      onNotify(
        `Recordatorio de ${period} aceptado por SendGrid para ${company.name}${result?.recipientEmail ? ` · ${result.recipientEmail}` : ''}.`,
        'success',
      );
    } catch (error) {
      onNotify(error.message || 'No se pudo enviar el recordatorio individual.', 'error');
    } finally {
      setBusyCompanyId('');
    }
  };

  const handleResendAccess = async (company) => {
    if (!window.confirm(`¿Enviar a ${company.email} un enlace para recuperar su acceso?`)) return;
    setBusyCompanyId(company.id);
    try {
      await resendCompanyAccess(company.uid);
      onNotify('Correo de acceso aceptado por SendGrid. Consulta la entrega en Historial y correos.', 'success');
    } catch (error) { onNotify(error.message || 'No se pudo reenviar el acceso.', 'error'); }
    finally { setBusyCompanyId(''); }
  };

  const handleReviewRegistration = async (company, decision) => {
    setBusyCompanyId(company.id);
    try {
      await onReviewRegistration(company.uid, decision);
      onNotify(decision === 'approved'
        ? 'Cuenta aprobada. Ya puede ingresar con la contraseña que eligió.'
        : 'Solicitud rechazada. El acceso permanece bloqueado.', 'success');
    } catch (error) {
      onNotify(error.message || 'No se pudo revisar la solicitud.', 'error');
    } finally {
      setBusyCompanyId('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.id || !formData.accessEmail || !formData.temporaryPassword) {
      onNotify('Nombre, ID, correo de acceso y contraseña temporal son obligatorios', 'error');
      return;
    }
    if (users.find(u => u.id === formData.id)) {
        onNotify('Ese ID de usuario ya existe.', 'error');
        return;
    }
    if (users.find(u => [u.email, u.contactEmail].filter(Boolean).includes(formData.accessEmail))) {
        onNotify('Ese correo de acceso ya está registrado.', 'error');
        return;
    }
    if (!isStrongPassword(formData.temporaryPassword)) {
      onNotify('La contraseña temporal debe tener 10 caracteres, mayúscula, minúscula y número.', 'error');
      return;
    }
    
    setIsCreating(true);
    try {
      const result = await onCreateUser({
        id: formData.id,
        name: formData.name,
        email: formData.accessEmail,
        temporaryPassword: formData.temporaryPassword,
        exportType: formData.exportType,
        role: 'company',
        stars: 0,
        destination: normalizeDestination(formData.destination),
        contactEmail: formData.contactEmail || formData.accessEmail,
        respondent: formData.respondent,
      });
      if (result?.welcomeEmailSent) {
        onNotify('Usuario creado. SendGrid aceptó las credenciales; revisa su entrega en Historial y correos.', 'success');
      } else if (result && result.welcomeEmailSent === false) {
        onNotify('Usuario creado. No se confirmó el envío del correo de bienvenida.', 'info');
      } else {
        onNotify('Usuario creado con éxito.', 'success');
      }
      setFormData(createEmptyCompanyForm());
      setPasswordCopied(false);
    } catch (error) {
      onNotify(error.message || 'No se pudo crear el usuario.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.temporaryPassword);
      setPasswordCopied(true);
      window.setTimeout(() => setPasswordCopied(false), 1800);
    } catch (error) {
      onNotify('No se pudo copiar automáticamente. Selecciona la contraseña y cópiala.', 'info');
    }
  };

  const handleTestEmail = async (event) => {
    event.preventDefault();
    if (!testEmail) {
      onNotify('Ingresa el correo que recibirá la prueba.', 'error');
      return;
    }
    setIsTestingEmail(true);
    setEmailTestResult(null);
    try {
      const result = await onTestTransactionalEmail(testEmail.trim());
      setEmailTestResult({ type: 'success', message: `SendGrid aceptó la prueba${result?.messageId ? ` · ID ${result.messageId}` : ''}.` });
      onNotify('Correo de prueba aceptado por SendGrid.', 'success');
    } catch (error) {
      setEmailTestResult({ type: 'error', message: error.message || 'La prueba de correo falló.' });
      onNotify(error.message || 'La prueba de correo falló.', 'error');
    } finally {
      setIsTestingEmail(false);
    }
  };
  
  const exportTypes = ["Banano", "Camarón", "Plátano", "Mango", "Brócoli", "Atún", "Flores", "Cacao", "Pescado", "Palmito en conserva", "Otros"];

  return (
    <div className="space-y-8">
      <section aria-labelledby="registration-requests-title" className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm md:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 id="registration-requests-title" className="text-xl font-semibold text-slate-900">Solicitudes de registro</h2>
            <p className="mt-1 text-sm text-slate-500">Revisa los datos de la empresa antes de habilitar su acceso. Cada persona ya eligió su contraseña.</p>
          </div>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-800">{pendingRegistrations.length}</span>
        </div>
        {!pendingRegistrations.length ? <p className="text-sm text-slate-500">No hay solicitudes pendientes.</p> : (
          <div className="divide-y divide-slate-100">
            {pendingRegistrations.map(company => (
              <div key={company.uid} className="flex flex-col justify-between gap-4 py-4 sm:flex-row sm:items-center">
                <div className="min-w-0 break-words">
                  <h3 className="font-semibold text-slate-900">{company.name}</h3>
                  <p className="text-sm text-slate-700">{company.respondent}</p>
                  <p className="text-sm text-slate-500">{company.email}</p>
                  <p className="mt-1 text-xs text-slate-500">{company.exportType || 'Sin sector'} · {normalizeDestination(company.destination)}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => handleReviewRegistration(company, 'approved')} disabled={Boolean(busyCompanyId)} className="rounded-xl bg-[#004AAD] px-4 py-2 text-sm font-semibold text-white hover:bg-[#003B8A] disabled:opacity-50">Aprobar</button>
                  <button type="button" onClick={() => handleReviewRegistration(company, 'rejected')} disabled={Boolean(busyCompanyId)} className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200/70 bg-white p-6 shadow-sm space-y-5">
          <div>
            <p className="text-xs uppercase text-slate-400">Alta rápida</p>
            <h2 className="text-xl font-semibold text-slate-900">Registrar nueva empresa</h2>
            <p className="text-sm text-slate-500">Una vez creada, recibirá un correo de bienvenida con sus credenciales de acceso.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700">ID único</label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleChange}
                className={adminFieldClass}
                placeholder="empresa_acme"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Nombre comercial</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={adminFieldClass}
                placeholder="Acme Foods"
                required
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700">Sector exportador</label>
              <select
                name="exportType"
                value={formData.exportType}
                onChange={handleChange}
                className={adminFieldClass}
              >
                {exportTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Destino principal</label>
              <select
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                className={adminFieldClass}
              >
                <option value="">Selecciona un destino</option>
                {DESTINATION_OPTIONS.map((destination) => (
                  <option key={destination} value={destination}>
                    {destination}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700">Correo de acceso</label>
              <input
                type="email"
                name="accessEmail"
                value={formData.accessEmail}
                onChange={handleChange}
                className={adminFieldClass}
                placeholder="usuario@empresa.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Contraseña temporal</label>
              <div className="relative">
                <input
                  type={showTemporaryPassword ? 'text' : 'password'}
                  name="temporaryPassword"
                  value={formData.temporaryPassword}
                  onChange={handleChange}
                  className={`${adminFieldClass} pr-20 font-mono`}
                  placeholder="10+ caracteres"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowTemporaryPassword(value => !value)}
                  className="absolute inset-y-0 right-0 mt-1 px-3 text-xs font-semibold text-[#004AAD]"
                >
                  {showTemporaryPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <button type="button" onClick={() => { setFormData(prev => ({ ...prev, temporaryPassword: generateTemporaryPassword() })); setPasswordCopied(false); }} className="font-semibold text-[#004AAD] hover:underline">
                  Generar otra
                </button>
                <button type="button" onClick={handleCopyPassword} className="font-semibold text-slate-500 hover:text-slate-800">
                  {passwordCopied ? 'Copiada ✓' : 'Copiar clave'}
                </button>
                <span className="text-slate-400">Se cambiará en el primer ingreso.</span>
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700">Correo logístico</label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className={adminFieldClass}
                placeholder="logistica@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700">Responsable</label>
              <input
                type="text"
                name="respondent"
                value={formData.respondent}
                onChange={handleChange}
                className={adminFieldClass}
                placeholder="Nombre y cargo"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={isCreating}
              className="w-full sm:flex-1 rounded-xl bg-[#004AAD] hover:bg-[#003B8A] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreating ? 'Creando acceso...' : 'Crear empresa y notificar'}
            </button>
            <button
              type="button"
              onClick={() =>
                setFormData(createEmptyCompanyForm())
              }
              className="w-full sm:w-auto rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Limpiar campos
            </button>
          </div>
        </form>
        <div className="rounded-xl border border-[#004AAD]/20 bg-[#004AAD] p-6 text-white shadow-sm space-y-5">
          <div>
            <p className="text-[0.6rem] uppercase text-white/60 mb-0.5">Estado de la base</p>
            <h2 className="text-xl font-semibold text-white">Panel de exportadores</h2>
            <p className="text-sm text-white/65">Monitorea la participación y prioriza nuevos registros.</p>
          </div>
          <dl className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <dt className="text-[0.6rem] uppercase text-white/60">Empresas activas</dt>
              <dd className="text-4xl font-bold">{activeCompanyUsers.length}</dd>
            </div>
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <dt className="text-[0.6rem] uppercase text-white/60">Sector líder</dt>
              <dd className="text-base font-semibold">{leadingSector}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-[0.6rem] uppercase text-white/60">Evaluación promedio</dt>
              <dd className="text-base font-semibold">{averageStars} ★</dd>
            </div>
          </dl>
          <div className="rounded-lg border border-white/15 bg-white/10 p-3.5 text-xs text-white/70 leading-relaxed">
            Depura duplicados y verifica correos para minimizar rebotes.
          </div>
          <form onSubmit={handleTestEmail} className="rounded-xl border border-white/15 bg-white/[0.08] p-4">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white/55">Diagnóstico SendGrid</p>
            <p className="mt-1 text-xs leading-relaxed text-white/60">Envía una prueba real y confirma que el proveedor la acepta.</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
              <input
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="correo@dominio.com"
                className="min-w-0 flex-1 rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
              />
              <button type="submit" disabled={isTestingEmail} className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-[#004AAD] hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60">
                {isTestingEmail ? 'Probando...' : 'Enviar prueba'}
              </button>
            </div>
            {emailTestResult && (
              <p className={`mt-3 text-xs leading-relaxed ${emailTestResult.type === 'success' ? 'text-emerald-200' : 'text-rose-200'}`}>
                {emailTestResult.message}
              </p>
            )}
          </form>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-100 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase text-slate-400">Directorio</p>
            <h2 className="text-xl font-semibold text-slate-900">Participantes registrados</h2>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 md:w-auto">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-2 text-xs font-semibold uppercase text-slate-500">
              Resultados
              <span className="text-base font-bold text-slate-900">{filteredUsers.length}</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-2 focus-within:border-[#004AAD] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#004AAD]/10 transition-colors">
              <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por empresa, ID o sector"
                className="w-full bg-transparent text-sm text-slate-600 focus:outline-none"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Empresa</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Sector</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Destino</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Contacto</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Estrellas</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Estado</th>
                <th className="px-4 py-3 text-right text-[0.65rem] font-semibold uppercase text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.id}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">{user.exportType || 'N/D'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">{normalizeDestination(user.destination)}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {user.contactEmail ? (
                      <button
                        onClick={() => navigator.clipboard?.writeText(user.contactEmail)}
                        className="text-sky-600 underline decoration-dotted decoration-sky-300"
                      >
                        {user.contactEmail}
                      </button>
                    ) : (
                      'N/D'
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-amber-500">
                    <div className="flex items-center gap-1">
                      {user.stars}
                      <StarIcon solid className="h-4 w-4" />
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      user.active === false
                        ? 'bg-slate-100 text-slate-500'
                        : 'bg-emerald-50 text-emerald-700'
                    }`}>
                      {user.registrationStatus === 'rejected' ? 'Rechazada' : user.active === false ? 'Desactivada' : 'Activa'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditCompany(user)}
                        disabled={busyCompanyId === user.id}
                        className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Editar
                      </button>
                      <button type="button" onClick={() => handleResendAccess(user)} disabled={busyCompanyId === user.id || user.active === false}
                        className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                        Reenviar acceso
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendCompanyReminder(user)}
                        disabled={busyCompanyId === user.id || user.active === false}
                        className="rounded-lg border border-[#004AAD]/20 px-2.5 py-1.5 text-xs font-semibold text-[#004AAD] hover:bg-[#004AAD]/5 disabled:cursor-not-allowed disabled:opacity-40"
                        title={user.active === false ? 'Activa la empresa antes de enviar comunicaciones.' : 'Enviar recordatorio del período actual solo a esta empresa.'}
                      >
                        {busyCompanyId === user.id ? 'Procesando...' : 'Recordar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCompany(user)}
                        disabled={busyCompanyId === user.id || countCompanySubmissions(user) > 0}
                        className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-slate-300"
                        title={countCompanySubmissions(user) > 0 ? 'Tiene envíos históricos; desactívala desde Editar.' : 'Eliminar empresa sin envíos.'}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-slate-500 text-sm">
                    No se encontraron empresas con ese criterio.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={!!selectedCompany}
        onClose={() => { setSelectedCompany(null); setEditForm(null); }}
        title={`Editar empresa · ${selectedCompany?.name || ''}`}
      >
        {selectedCompany && editForm && (
          <form onSubmit={handleSaveCompany} className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p><span className="font-semibold text-slate-800">ID:</span> {selectedCompany.id}</p>
              <p className="mt-1">
                <span className="font-semibold text-slate-800">Historial:</span>{' '}
                {countCompanySubmissions(selectedCompany)} envío{countCompanySubmissions(selectedCompany) === 1 ? '' : 's'}
              </p>
              <p className="mt-2 text-xs text-slate-500">El ID permanece fijo para no romper formularios ni resultados históricos.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">
                Nombre comercial
                <input name="name" value={editForm.name} onChange={handleEditChange} className={adminFieldClass} required />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Correo de acceso
                <input type="email" name="email" value={editForm.email} onChange={handleEditChange} className={adminFieldClass} required />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Correo de contacto
                <input type="email" name="contactEmail" value={editForm.contactEmail} onChange={handleEditChange} className={adminFieldClass} required />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Responsable
                <input name="respondent" value={editForm.respondent} onChange={handleEditChange} className={adminFieldClass} />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Sector exportador
                <select name="exportType" value={editForm.exportType} onChange={handleEditChange} className={adminFieldClass}>
                  {exportTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Destino principal
                <select name="destination" value={editForm.destination} onChange={handleEditChange} className={adminFieldClass}>
                  <option value="">Selecciona un destino</option>
                  {DESTINATION_OPTIONS.map(destination => <option key={destination} value={destination}>{destination}</option>)}
                </select>
              </label>
            </div>
            <label className={`flex items-start gap-3 rounded-xl border p-4 ${
              editForm.active ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'
            }`}>
              <input
                type="checkbox"
                name="active"
                checked={editForm.active}
                disabled={Boolean(selectedCompany.registrationStatus && selectedCompany.registrationStatus !== 'approved')}
                onChange={handleEditChange}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#004AAD] focus:ring-[#004AAD]"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-800">Acceso activo</span>
                <span className="block text-xs leading-relaxed text-slate-500">
                  Al desactivarlo, la empresa no podrá iniciar sesión; sus formularios e historial se conservan.
                </span>
              </span>
            </label>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => { setSelectedCompany(null); setEditForm(null); }}
                className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busyCompanyId === selectedCompany.id}
                className="rounded-xl bg-[#004AAD] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#003B8A] disabled:opacity-50"
              >
                {busyCompanyId === selectedCompany.id ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminUserManagement;
